/**
 * Distinctive tokens used to place a citation chip and to decide whether a
 * snippet actually supports the sentence we would mark.
 *
 * Cache scope: none — local string compare, not an LLM prompt.
 */

const STOP_WORDS = new Set([
  'that',
  'this',
  'with',
  'from',
  'have',
  'were',
  'been',
  'their',
  'there',
  'about',
  'would',
  'could',
  'should',
  'which',
  'while',
  'where',
  'when',
  'what',
  'your',
  'into',
  'than',
  'then',
  'them',
  'they',
  'also',
  'more',
  'most',
  'some',
  'such',
  'only',
  'over',
  'under',
  'after',
  'before',
  'between',
  'among',
  'across',
  'adults',
  'ages',
  'consumers',
  'consumer',
  'snack',
  'snacks',
  'snacking',
])

const PERCENT_RE = /\d+(?:\.\d+)?%/g
const NUMBER_RE = /\b\d{2,}(?:\.\d+)?\b/g
const WORD_RE = /\b[A-Za-z][A-Za-z0-9&'-]{3,}\b/g

/**
 * Prefer percentages and multi-digit numbers, then longer non-stop words.
 * Shared with chip inference so placement and the support gate use one lexicon.
 */
export function extractPlacementTokens(snippet: string): Array<string> {
  const tokens: Array<string> = []
  const percent = snippet.match(PERCENT_RE) || []
  tokens.push(...percent)

  const numbers = snippet.match(NUMBER_RE) || []
  for (const n of numbers) {
    if (!tokens.includes(n)) {
      tokens.push(n)
    }
  }

  const words =
    snippet.match(WORD_RE)?.filter((w) => !STOP_WORDS.has(w.toLowerCase())) || []
  words.sort((a, b) => b.length - a.length)
  for (const w of words.slice(0, 8)) {
    if (!tokens.includes(w)) {
      tokens.push(w)
    }
  }

  return tokens
}

function isNumericToken(token: string): boolean {
  return /^\d+(?:\.\d+)?%$/.test(token) || /^\d{2,}(?:\.\d+)?$/.test(token)
}

/**
 * True when `snippet` shares a number/percent or at least two distinctive
 * words (length ≥ 5, not stop words) with `claim`. Generic snack vocabulary
 * does not count.
 */
export function citationSupportsClaim(claim: string, snippet: string): boolean {
  const claimTokens = extractPlacementTokens(claim)
  const snippetTokens = extractPlacementTokens(snippet)
  if (claimTokens.length === 0 || snippetTokens.length === 0) {
    return false
  }

  const snippetNumeric = new Set(
    snippetTokens.filter((token) => isNumericToken(token)).map((token) => token),
  )
  for (const token of claimTokens) {
    if (isNumericToken(token) && snippetNumeric.has(token)) {
      return true
    }
  }

  const snippetWords = new Set(
    snippetTokens
      .filter((token) => !isNumericToken(token) && token.length >= 5)
      .map((token) => token.toLowerCase()),
  )
  let sharedWords = 0
  const seen = new Set<string>()
  for (const token of claimTokens) {
    if (isNumericToken(token) || token.length < 5) {
      continue
    }
    const key = token.toLowerCase()
    if (seen.has(key) || !snippetWords.has(key)) {
      continue
    }
    seen.add(key)
    sharedWords += 1
    if (sharedWords >= 2) {
      return true
    }
  }

  return false
}
